/**
 * @file schemas.ts
 * @brief Zod shapes for a personal user's billing endpoints.
 *
 * @details
 * - GET  /billing/customer            → saved cards + default (personal: no `company_id`)
 * - GET  /billing/subscriptions       → plan, minutes window, catalogue, list rates
 * - POST /billing/subscriptions       → { subscriptionId, status, clientSecret }
 * - POST /billing/subscriptions/sync  → status re-read from Stripe
 * - POST /billing/subscriptions/cancel
 * - GET  /sessions/search/quota       → Ask allowance
 * - GET  /invoices                    → unified feed (own rows for a personal user)
 * Loose (`passthrough`, optional) on purpose: these are read by the phone too
 * and grow additively.
 * Related: BACKEND src/features/billing/routes.js, subscription.service.mjs
 *
 * @author DES
 * @date 17-10-2026
 */

import { z } from "zod";

const num = z.union([
  z.number(),
  z
    .string()
    .transform((v) => (v === "" ? 0 : Number(v)))
    .pipe(z.number()),
]);
const optNum = num.nullable().optional();

/** Why this account can't buy (B1, 22-10-2026): COUNTRY_NOT_AVAILABLE or SUBSCRIPTIONS_PAUSED; null = it can. */
const NotAvailableSchema = z
  .object({ code: z.string(), message: z.string().nullable().optional() })
  .nullable()
  .optional();

export const CardSchema = z.object({
  id: z.string(),
  brand: z.string(),
  last4: z.string(),
  expMonth: z.number(),
  expYear: z.number(),
});
export type Card = z.infer<typeof CardSchema>;

export const CardsResponseSchema = z.object({
  customerId: z.string().optional(),
  defaultPaymentMethod: z.string().nullable(),
  cards: z.array(CardSchema).default([]),
});
export type CardsResponse = z.infer<typeof CardsResponseSchema>;

export const SetupIntentSchema = z.object({ clientSecret: z.string() });

export const CatalogPlanSchema = z
  .object({
    code: z.string(),
    name: z.string(),
    currency: z.string(),
    priceMinor: optNum.transform((v) => v ?? null),
    annualPriceMinor: optNum,
    annualMonthlyEquivalentMinor: optNum,
    annualSavingPct: optNum,
    includedMinutes: optNum,
    includedInterpretationMinutes: optNum,
    unlimitedNotes: z.boolean().optional(),
    notesFairUseMinutes: optNum,
    s2sRateMinor: optNum,
    realtimeRateMinor: optNum,
    enterprise: z.boolean().optional(),
    contactSales: z.boolean().optional(),
    // Launch pricing (DES 18-10-2026; proposed contract). Absent on today's
    // backend; null where the plan is unlimited.
    perSeat: z.boolean().nullable().optional(),
    minSeats: optNum,
    notesMinutes: optNum,
    pooled: z.boolean().nullable().optional(),
    overageRateMinor: optNum,
    noPlanMinChargeMinor: optNum,
    historyDays: optNum,
    askLimit: optNum,
    team: z.boolean().nullable().optional(),
    // Free row of `compare` (BACKEND 21-10-2026): per-session minimum, and
    // false while FREE_PLAN_ENABLED is off.
    minChargeMinor: optNum,
    available: z.boolean().nullable().optional(),
    // B20 (24-10-2026): Free "monthly" | "one_time" (numbers a total, once)
    // and its expiry in days (null = never).
    resetMode: z.string().nullable().optional(),
    expiryDays: optNum,
    // B43 (31-10-2026): Free = one pool for Notes or Full Intelligence, never charged.
    onePool: z.boolean().nullable().optional(),
    intelMaxMinutes: optNum,
    payAsYouGo: z.boolean().nullable().optional(),
  })
  .passthrough();
export type CatalogPlanRow = z.infer<typeof CatalogPlanSchema>;

/**
 * GET /billing/subscriptions/catalog: the plans on sale (`catalog`) and, since
 * BACKEND 21-10-2026, `compare` = the Free row + the same plans, for the
 * Compare plans table. Older backends send no `compare`.
 */
export const PlanCatalogSchema = z.object({
  eligible: z.boolean().optional(),
  notAvailable: NotAvailableSchema,
  currency: z.string().optional(),
  catalog: z.array(CatalogPlanSchema).default([]),
  compare: z.array(CatalogPlanSchema).optional(),
});
export type PlanCatalog = z.infer<typeof PlanCatalogSchema>;

export const SubscriptionSchema = z
  .object({
    id: z.union([z.string(), z.number()]).optional(),
    planCode: z.string(),
    status: z.string(),
    entitled: z.boolean().optional(),
    currency: z.string().nullable().optional(),
    currentPeriodStart: z.string().nullable().optional(),
    currentPeriodEnd: z.string().nullable().optional(),
    billingInterval: z.string().nullable().optional(),
    cancelAtPeriodEnd: z.boolean().nullable().optional(),
    stripeSubscriptionId: z.string().nullable().optional(),
    companyTeamPlanId: z.string().nullable().optional(),
    /** Card-free Pro trial (ADR-045; TRIAL_ENABLED off at launch). */
    trial: z.boolean().optional(),
    trialEndsAt: z.string().nullable().optional(),
    /** Stripe's upcoming invoice, when the server sends it (Part 1.2). */
    nextBillDate: z.string().nullable().optional(),
    nextBillAmount: optNum,
    /** Account credit Stripe applies to that bill, minor units (when the server sends it). */
    nextBillCredit: optNum,
  })
  .passthrough();
export type Subscription = z.infer<typeof SubscriptionSchema>;

const PoolSchema = z.object({
  used: num,
  included: num,
  remaining: num,
  /** Intelligence past the allowance so far this period (launch pricing). */
  overageMinutes: optNum,
  overageCents: optNum,
});
export type UsagePool = z.infer<typeof PoolSchema>;

export const UsageSchema = z
  .object({
    /** Per pool (BACKEND A3, 18-10-2026): notes only while NOTES_POOL_ENABLED. */
    pools: z
      .object({
        intelligence: PoolSchema.nullable().optional(),
        notes: PoolSchema.nullable().optional(),
        interpretation: PoolSchema.nullable().optional(),
      })
      .nullable()
      .optional(),
    usedMinutes: num,
    includedMinutes: num,
    remainingMinutes: num,
    periodStart: z.string().nullable().optional(),
    periodEnd: z.string().nullable().optional(),
  })
  .passthrough();
export type Usage = z.infer<typeof UsageSchema>;

export const BasicRatesSchema = z
  .object({
    currency: z.string(),
    s2sRateMinor: optNum,
    realtimeRateMinor: optNum,
    boardroomRateMinor: optNum,
    notesRateMinor: optNum,
    speakerFeeMinor: optNum,
  })
  .passthrough();

export const SubscriptionStatusSchema = z
  .object({
    success: z.boolean().optional(),
    eligible: z.boolean().optional(),
    notAvailable: NotAvailableSchema,
    currency: z.string().optional(),
    catalog: z.array(CatalogPlanSchema).default([]),
    basic: BasicRatesSchema.nullable().optional(),
    subscription: SubscriptionSchema.nullable().optional(),
    usage: UsageSchema.nullable().optional(),
    billingScope: z.string().optional(),
    /** A user on the Free tier (ADR-045; null while FREE_PLAN_ENABLED is off). */
    freePlan: z
      .object({
        notesMinutes: num,
        notesUsed: num,
        notesRemaining: num,
        askLimit: optNum,
        historyDays: optNum,
        periodStart: z.string().nullable().optional(),
        periodEnd: z.string().nullable().optional(),
        /** Blocked country (B1 addendum 2): no free minutes; show its message, never "0 of 0". */
        notAvailable: NotAvailableSchema,
        /** B4 (22-10-2026): false = the first free recording waits for the email link. */
        emailVerified: z.boolean().optional(),
        /** B20: "monthly" | "one_time"; one-time numbers are a total, once. */
        resetMode: z.string().nullable().optional(),
        expiresAt: z.string().nullable().optional(),
        expired: z.boolean().nullable().optional(),
        /** B43: what Full Intelligence may still use of the pool. */
        intelRemainingMinutes: optNum,
        /** B43: one pool for Notes or Full Intelligence; present = B43 backend. */
        poolMinutesLeft: optNum,
        grantRefused: z.object({ reason: z.string(), message: z.string() }).nullable().optional(),
      })
      .nullable()
      .optional(),
    /**
     * Free plan minutes this UTC month (B4, 22-10-2026); null when not on
     * Free. `used` can pass `limit` (a running recording is never cut).
     */
    freeMinutes: z
      .object({
        used: z.number(),
        limit: z.number(),
        resetsAt: z.string(),
        blocked: z.boolean(),
        emailVerified: z.boolean(),
        // B20 (24-10-2026): one-time Free. Absent on an older backend = monthly.
        // `resetsAt` stays the next 1st for old builds; read `nextResetAt`.
        resetMode: z.string().nullable().optional(),
        totalMinutes: optNum,
        usedMinutes: optNum,
        remainingMinutes: optNum,
        asksTotal: optNum,
        asksUsed: optNum,
        expiresAt: z.string().nullable().optional(),
        expired: z.boolean().nullable().optional(),
        nextResetAt: z.string().nullable().optional(),
        payAsYouGoAfter: z.boolean().nullable().optional(),
        // B43 (31-10-2026): one pool for Meeting Notes or Full Intelligence.
        poolMinutesLeft: optNum,
        intelMaxMinutes: optNum,
        intelCapped: z.boolean().nullable().optional(),
        intelUsedMinutes: optNum,
        intelRemainingMinutes: optNum,
        graceMinutes: optNum,
        warnAtMinutesLeft: optNum,
        /** Free minutes refused once per device / disposable email. */
        grantRefused: z
          .object({ reason: z.string(), message: z.string() })
          .nullable()
          .optional(),
      })
      .nullable()
      .optional(),
    /**
     * Pro / Business Meeting Notes fair use this plan month (B6,
     * 22-10-2026); null = no ceiling. `used` can pass `limit`.
     */
    fairUse: z
      .object({
        used: z.number(),
        limit: z.number(),
        resetsAt: z.string(),
        blocked: z.boolean(),
      })
      .nullable()
      .optional(),
    /**
     * B39 (28-10-2026): this user's history window. `from` = the cutoff the
     * 403 HISTORY_LOCKED uses (ISO); null = no limit. Older backends omit it.
     */
    history: z
      .object({ days: z.number().nullable().optional(), from: z.string().nullable().optional() })
      .nullable()
      .optional(),
    /** B43 (31-10-2026): false = Pro / Business extra Intelligence minutes are off (refused, never charged). */
    planOverage: z.boolean().nullable().optional(),
    /** A company's Business plan today (teamPlan.service serializeTeamPlan). */
    teamPlan: z
      .object({
        planCode: z.string().nullable().optional(),
        status: z.string().nullable().optional(),
        quantity: optNum,
        seatCount: optNum,
        currency: z.string().nullable().optional(),
        currentPeriodEnd: z.string().nullable().optional(),
        cancelAtPeriodEnd: z.boolean().nullable().optional(),
        interval: z.string().nullable().optional(),
        stripeSubscriptionId: z.string().nullable().optional(),
      })
      .passthrough()
      .nullable()
      .optional(),
    /** The company plan card's one read (plans-visible brief Part 1, 21-10-2026). */
    companyPlan: z
      .object({
        planCode: z.string().nullable().optional(),
        interval: z.string().nullable().optional(),
        status: z.string().nullable().optional(),
        seats: optNum,
        minSeats: optNum,
        pricePerSeat: optNum,
        currency: z.string().nullable().optional(),
        taxBehavior: z.string().nullable().optional(),
        nextBillDate: z.string().nullable().optional(),
        nextBillAmount: optNum,
        nextBillCredit: optNum,
        cancelAtPeriodEnd: z.boolean().nullable().optional(),
        includedIntelMinutes: optNum,
        intelMinutesUsed: optNum,
        periodEnd: z.string().nullable().optional(),
        /** Price per seat by interval, minor units (ADR-049). */
        prices: z.object({ month: optNum, year: optNum }).partial().nullable().optional(),
      })
      .passthrough()
      .nullable()
      .optional(),
    enterprise: z.unknown().optional(),
    /** The server's window for users with no plan (BACKEND A3). */
    period: z.object({ start: z.string(), end: z.string() }).nullable().optional(),
    /** Plan change answers (BACKEND A2): 3DS on the proration, or still pending. */
    clientSecret: z.string().nullable().optional(),
    pending: z.boolean().optional(),
    subscriptionId: z.string().optional(),
  })
  .passthrough();
export type SubscriptionStatus = z.infer<typeof SubscriptionStatusSchema>;

export const SubscribeResponseSchema = z
  .object({
    subscriptionId: z.string(),
    status: z.string().nullable().optional(),
    clientSecret: z.string().nullable().optional(),
    planCode: z.string().optional(),
    billingInterval: z.string().optional(),
  })
  .passthrough();

export const SearchQuotaSchema = z.object({
  success: z.boolean().optional(),
  enabled: z.boolean().optional(),
  quota: z
    .object({
      limit: num,
      used: num,
      remaining: num,
      planCode: z.string().optional(),
      periodEnd: z.string().nullable().optional(),
      enforced: z.boolean().default(false),
      /** B20: Free "one_time" = the questions are a total, once. */
      resetMode: z.string().nullable().optional(),
      expiresAt: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
});
export type SearchQuota = z.infer<typeof SearchQuotaSchema>;

export const PricingSchema = z
  .object({
    currencyCode: z.string(),
    ratePerMin: z.number(),
    minChargeMajor: z.number().optional(),
    taxRate: z.number().optional(),
  })
  .passthrough();

export const InvoiceRowSchema = z
  .object({
    id: z.string(),
    product: z.string().nullable().optional(),
    amount: num,
    currency: z.string().nullable().optional(),
    status: z.string().nullable().optional(),
    createdAt: z.string().optional(),
    // Additive fields the report asks the backend for; read when present.
    chargedCents: optNum,
    capturedCents: optNum,
    minutes: optNum,
    durationSeconds: optNum,
    paymentFailed: z.boolean().nullable().optional(),
    refunded: z.boolean().nullable().optional(),
    refundedCents: optNum,
    billedCents: optNum,
    deleted: z.boolean().nullable().optional(),
    disputeStatus: z.string().nullable().optional(),
    // The server's status (21-10-2026): paid, partial, open, failed,
    // refunded, part_refunded, included, credit.
    paymentStatus: z.string().nullable().optional(),
  })
  .passthrough();
export type InvoiceRow = z.infer<typeof InvoiceRowSchema>;

export const InvoicesResponseSchema = z.object({
  total: num.optional(),
  page: z.number().optional(),
  pageSize: z.number().optional(),
  subscriptionAmountCents: num.optional(),
  totalsByCurrency: z
    .array(z.object({ currency: z.string(), amountCents: num, openAmountCents: num.optional(), count: num.optional() }))
    .optional(),
  items: z.array(InvoiceRowSchema).default([]),
});
export type InvoicesResponse = z.infer<typeof InvoicesResponseSchema>;
